import { ReactNode } from "react";
import cn from "classnames";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/20/solid";

interface StatTileProps {
  label: string;
  value: ReactNode;
  // Percent change against the previous period. null/undefined hides it
  // (nothing to compare against), 0 shows a flat dash.
  delta?: number | null;
  // Small line under the value ("12 officers", "since Nov 3").
  hint?: ReactNode;
  // Short pill next to the label, e.g. "new".
  badge?: string;
  // Dims the tile when the value is zero so the live ones stand out.
  muted?: boolean;
  // Highlights the tile as the current selection (e.g. the charted metric).
  selected?: boolean;
  // Makes the whole tile a button.
  onClick?: () => void;
  className?: string;
}

function DeltaChip({ delta }: { delta: number }) {
  if (delta === 0) return <span className="text-xs text-base-content/40">no change</span>;
  const up = delta > 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
        up ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
      )}
    >
      {up ? <ArrowUpIcon className="size-3" /> : <ArrowDownIcon className="size-3" />}
      {Math.abs(delta) >= 1000 ? ">999" : Math.round(Math.abs(delta))}%
    </span>
  );
}

export default function StatTile({ label, value, delta, hint, badge, muted, selected, onClick, className }: StatTileProps) {
  const body = (
    <>
      <div className="flex items-center gap-2 min-w-0">
        <p className="m-0 text-xs font-medium text-base-content/60 truncate">{label}</p>
        {badge && (
          <span className="shrink-0 rounded-full bg-blue-50 dark:bg-blue-900/40 px-1.5 py-px text-[10px] font-medium text-blue-600 dark:text-blue-300">
            {badge}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <p className="m-0 text-2xl font-semibold text-base-content tabular-nums">{value}</p>
        {delta !== undefined && delta !== null && <DeltaChip delta={delta} />}
      </div>
      {hint && <p className="m-0 mt-0.5 text-xs text-base-content/50 truncate">{hint}</p>}
    </>
  );

  const classes = cn(
    "rounded-xl border bg-base-100 p-3 text-left w-full min-w-0 transition-colors",
    selected ? "border-blue-500 ring-1 ring-blue-500" : "border-base-300",
    muted && !selected && "opacity-60",
    onClick && "cursor-pointer hover:border-blue-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
    className
  );

  return onClick ? (
    <button type="button" onClick={onClick} aria-pressed={selected} className={classes}>
      {body}
    </button>
  ) : (
    <div className={classes}>{body}</div>
  );
}
